// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'hist... Remove this comment to see the full error message
import { type Location } from 'history';
import replace from 'lodash/replace';

import { type State, type ValueOf } from 'src/types';

import { SourceTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getAnnotationsById } from '../EntityUtils';
import { getPlaylistFromState } from '../MusicUtils';

import {
  ARTIST_BASE_PATH,
  CURATOR_BASE_PATH,
  PODCAST_BASE_PATH,
  STATION_BASE_PATH,
} from './shared';

interface IBackstageRouteParams {
  path: string;
  pandoraId: string;
}

/*
 * Place a hint in the routing state indicating the pandoraId that is being routed to.
 * The pandoraId is used by a saga to pre-fetch the related data
 *
 * @param {String} path - url location
 * @param {String} pandoraId - hint to place in routing state
 * @return {Object} Location-like object for routing
 */
export function getBackstageLocation({ path, pandoraId }: IBackstageRouteParams): string {
  const location: Location = {
    pathname: path,
  };

  if (pandoraId) {
    location.state = {
      pandoraId,
    };
  }
  return location;
}

/**
 * Get the pandoraId hint from a location routing
 *
 * @param {object} state
 * @returns {string} pandoraId
 */
export function getBackstagePandoraIdFromLocation(state: State) {
  return state.routing.locationBeforeTransitions?.state?.pandoraId;
}

function spliceBackstageChildPagePath(token = '', childPageName: string) {
  const backstagePathArray = token.split('/');
  backstagePathArray.splice(2, 0, childPageName);
  return backstagePathArray.join('/');
}

export function getBackstageArtistAllSongsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'all-songs');
}

export function getBackstageArtistAllAlbumsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'all-albums');
}

export function getBackstageAllPlaylistsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'all-playlists');
}

export function getBackstageAllStationsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'all-stations');
}

export function getBackstageFullBioPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'full-bio');
}

export function getBackstageTrackSimilarSongsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'similar-songs');
}

export function getBackstageTrackLyricsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'lyrics');
}

export function getBackstageTrackCreditsPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'credits');
}

export function getBackstageFullDescriptionPathForShareUrl(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'description');
}

export function getBackstagePodcastProgramAllEpisodesPath(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'all-episodes');
}

export function getBackstagePodcastProgramThumbedUpEpisodesPath(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'thumbed-up-episodes');
}

export function getBackstagePodcastProgramThumbedDownEpisodesPath(shareUrl: string) {
  return spliceBackstageChildPagePath(shareUrl, 'thumbed-down-episodes');
}

export function getBackstageCollectedStationAllThumbsUpPath(stationId: string) {
  return `${STATION_BASE_PATH}/thumbed-up/${stationId}` as const;
}

export function getBackstageCollectedStationAllThumbsDownPath(stationId: string) {
  return `${STATION_BASE_PATH}/thumbed-down/${stationId}` as const;
}

export function getBackstageCollectedStationAllSeedsPath(stationId: string) {
  return `${STATION_BASE_PATH}/seeds/${stationId}` as const;
}

export function getBackstageGenrePath(genreId: string) {
  return `/genre/${genreId}` as const;
}

interface IBackstagePodcastProgramPathParams {
  programName: string;
  podcastId: string;
}

export function getBackstagePodcastProgramPath({
  programName,
  podcastId,
}: IBackstagePodcastProgramPathParams) {
  return `${PODCAST_BASE_PATH}/${programName}/${podcastId}` as const;
}

export function getBackstageCuratorPath(curatorId: string) {
  return `${CURATOR_BASE_PATH}/${curatorId}` as const;
}

export function getBackstagePathFromPlaybackPath(
  playbackPath: string,
  annotations: $TSFixMe,
): string {
  const urlTokens = playbackPath.split('/');
  const pandoraId = urlTokens[urlTokens.length - 1];

  if (pandoraId.indexOf(':') !== -1 && urlTokens.indexOf('playlist') === -1) {
    return annotations.getIn([pandoraId, 'shareableUrlPath']);
  }

  return replace(playbackPath, 'play/', '');
}

interface IGetBackstageArtistUrl {
  artistSeoToken: string;
}

export function getBackstageArtistPath({ artistSeoToken }: IGetBackstageArtistUrl) {
  return `${ARTIST_BASE_PATH}/${artistSeoToken}` as const;
}

interface IBackstageSeoTokenUrlParams {
  artistSeoToken: string;
  curatorSeoToken: string;
  albumSeoToken?: string;
  trackSeoToken?: string;
  pandoraId?: string;
}

export function getBackstageSeoTokenUrl({
  artistSeoToken,
  albumSeoToken,
  curatorSeoToken,
  trackSeoToken,
  pandoraId,
}: IBackstageSeoTokenUrlParams) {
  let url = '';
  if (trackSeoToken) {
    url = `${artistSeoToken}/${albumSeoToken}/${trackSeoToken}`;
  } else if (albumSeoToken) {
    url = `${artistSeoToken}/${albumSeoToken}`;
  } else if (artistSeoToken) {
    url = artistSeoToken;
  } else if (curatorSeoToken) {
    url = curatorSeoToken;
  }
  url += pandoraId ? `/${pandoraId}` : '';
  return url;
}

export function isBackstageGenrePath(path: string): boolean {
  return /^\/music\/.+\/.+/.test(path) || /^\/genre\/.+/.test(path);
}

export function isBackstageArtistPath(path: string): boolean {
  return /^\/artist(?!\/play\/)(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistFullBioPath(path: string): boolean {
  return /^\/artist\/full-bio(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistAllSongsPath(path: string): boolean {
  return /^\/artist\/all-songs(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistAllAlbumsPath(path: string): boolean {
  return /^\/artist\/all-albums(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistAllStationsPath(path: string): boolean {
  return /^\/artist\/all-stations(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistAllPlaylistsPath(path: string): boolean {
  return /^\/artist\/all-playlists(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageArtistChildPagePath(path: string): boolean {
  return (
    isBackstageArtistFullBioPath(path) ||
    isBackstageArtistAllSongsPath(path) ||
    isBackstageArtistAllAlbumsPath(path) ||
    isBackstageArtistAllStationsPath(path) ||
    isBackstageArtistAllPlaylistsPath(path)
  );
}

export function isBackstageCuratorPath(path: string): boolean {
  return /^\/curator\/[^/]+\/(CU:[^/]+)$/.test(path);
}

export function isBackstageCuratorFullBioPath(path: string): boolean {
  return /^\/curator\/full-bio\/[^/]+\/(CU:[^/]+)$/.test(path);
}

export function isBackstageCuratorAllStationsPath(path: string): boolean {
  return /^\/curator\/all-stations\/[^/]+\/(CU:[^/]+)$/.test(path);
}

export function isBackstageCuratorAllPlaylistsPath(path: string): boolean {
  return /^\/curator\/all-playlists\/[^/]+\/(CU:[^/]+)$/.test(path);
}

export function isBackstageCuratorChildPath(path: string): boolean {
  return (
    isBackstageCuratorFullBioPath(path) ||
    isBackstageCuratorAllStationsPath(path) ||
    isBackstageCuratorAllPlaylistsPath(path)
  );
}

export function isBackstageAlbumPath(path: string): boolean {
  return (
    /^\/artist(?!\/play\/)(\/[^A-Z/]+){2}(\/[A-Z]{2}[^/]+)?$/.test(path) &&
    !isBackstageArtistChildPagePath(path)
  );
}

export function isBackstageAlbumDescriptionPath(path: string): boolean {
  return /^\/artist\/description(\/[^A-Z/]+){2}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageAlbumChildPagePath(path: string): boolean {
  return isBackstageAlbumDescriptionPath(path);
}

export function isBackstageTrackPath(path: string): boolean {
  return (
    /^\/artist(?!\/play\/)(\/[^A-Z/]+){3}(\/[A-Z]{2}[^/]+)?$/.test(path) &&
    !isBackstageAlbumChildPagePath(path)
  );
}

export function isBackstageTrackLyricsPath(path: string): boolean {
  return /^\/artist\/lyrics(\/[^A-Z/]+){3}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageTrackCreditsPath(path: string): boolean {
  return /^\/artist\/credits(\/[^A-Z/]+){3}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageTrackSimilarSongsPath(path: string): boolean {
  return /^\/artist\/similar-songs(\/[^A-Z/]+){3}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstageTrackChildPagePath(path: string): boolean {
  return (
    isBackstageTrackLyricsPath(path) ||
    isBackstageTrackSimilarSongsPath(path) ||
    isBackstageTrackCreditsPath(path)
  );
}

export function isBackstageChildPagePath(path: string): boolean {
  return (
    isBackstageArtistChildPagePath(path) ||
    isBackstageAlbumChildPagePath(path) ||
    isBackstageTrackChildPagePath(path) ||
    isBackstageCuratorChildPath(path)
  );
}

export function isPlaylistPath(path: string): boolean {
  return /^\/playlist\/PL:\d+:\d+$/.test(path);
}

export function shouldShowPremiumAccessCoachmarkOnBSPPath(path: string): boolean {
  return (
    isBackstageAlbumPath(path) ||
    isBackstageAlbumChildPagePath(path) ||
    isBackstageTrackPath(path) ||
    isBackstageTrackChildPagePath(path) ||
    isPlaylistPath(path)
  );
}

export function getPandoraIdAndSourceTypeFromBackstagePath(
  state: State,
  path: string,
): { pandoraId?: string; sourceType: ValueOf<typeof SourceTypes> | undefined } {
  let pandoraId;
  let sourceType: ValueOf<typeof SourceTypes> | undefined = undefined;

  if (!path) {
    return { pandoraId, sourceType };
  }

  const processedPath = path.replace(/[/](?:description|lyrics|similar-songs|credits)/g, '');
  const splitPath = processedPath.replace(/^[/]?/, '').split('/');
  const [seoToken1, seoToken2, seoToken3, seoToken4, seoToken5] = splitPath;
  const trackOrAlbumSeoToken = seoToken5 || seoToken4;

  let annotation;
  if (seoToken1 !== 'podcast' && trackOrAlbumSeoToken) {
    sourceType = seoToken5 ? SourceTypes.SONG : SourceTypes.ALBUM;
    annotation = getInDomain('catalog.utils.getAnnotationByEncryptedPandoraId')(
      state,
      trackOrAlbumSeoToken,
    );
  } else if (seoToken1 === 'playlist' && seoToken2) {
    sourceType = SourceTypes.PLAYLIST;
    annotation =
      getPlaylistFromState(state, seoToken2) ||
      getInDomain('catalog.utils.getAnnotationByEncryptedPandoraId')(state, seoToken2);
  } else if (seoToken1 === 'artist' && seoToken3) {
    sourceType = SourceTypes.ARTIST;
    annotation = getInDomain('catalog.utils.getAnnotationByEncryptedPandoraId')(state, seoToken3);
  } else if (seoToken1 === 'curator' && seoToken3) {
    sourceType = SourceTypes.CURATOR;
    annotation = getInDomain('catalog.utils.getAnnotationByEncryptedPandoraId')(state, seoToken3);
  } else if (seoToken1 === 'podcast' && seoToken3) {
    sourceType = seoToken4 ? SourceTypes.EPISODE : SourceTypes.PODCAST;
    const sourceId = seoToken4 || seoToken3;
    annotation = getAnnotationsById(state, sourceId);
  }

  if (annotation && annotation.size !== 0) {
    pandoraId = annotation.get('pandoraId');
  }

  return { pandoraId, sourceType };
}

export function isBackstageStationPath(path: string): boolean {
  return /^\/station(?!\/play\/)(\/[^A-Z/]+){1}(\/[A-Z]{2}[^/]+)?$/.test(path);
}

export function isBackstagePodcastProgramDescriptionPath(path: string): boolean {
  return /^\/podcast\/description\/[^/]+\/(PC:[^/]+)?$/.test(path);
}

export function isBackstagePodcastEpisodeDescriptionPath(path: string): boolean {
  return /^\/podcast\/description\/[^/]+\/[^/]+\/(PE:[^/]+)?$/.test(path);
}

export function isBackstagePodcastThumbedUpEpisodesPath(path: string): boolean {
  return /^\/podcast\/thumbed-up-episodes\/[^/]+\/(PC:[^/]+)?$/.test(path);
}

export function isBackstagePodcastThumbedDownEpisodesPath(path: string): boolean {
  return /^\/podcast\/thumbed-down-episodes\/[^/]+\/(PC:[^/]+)?$/.test(path);
}

export function isBackstagePodcastAllEpisodesPath(path: string): boolean {
  return /^\/podcast\/all-episodes\/[^/]+\/(PC:[^/]+)?$/.test(path);
}

export function isBackstagePodcastChildPath(path: string): boolean {
  const isDescriptionPath =
    isBackstagePodcastProgramDescriptionPath(path) ||
    isBackstagePodcastEpisodeDescriptionPath(path);
  const isThumbedPath =
    isBackstagePodcastThumbedUpEpisodesPath(path) ||
    isBackstagePodcastThumbedDownEpisodesPath(path);
  return isBackstagePodcastAllEpisodesPath(path) || isDescriptionPath || isThumbedPath;
}

export function isBackstagePodcastProgramPath(path: string): boolean {
  return (
    /^\/podcast(?!\/play\/)(\/[^/]+\/)(PC:[^/]+)$/.test(path) && !isBackstagePodcastChildPath(path)
  );
}

export function isBackstagePodcastEpisodePath(path: string): boolean {
  return (
    /^\/podcast(?!\/play\/)(\/[^/]+\/[^/]+\/)(PE:[^/]+)$/.test(path) &&
    !isBackstagePodcastChildPath(path)
  );
}

export function isBackstagePodcastParentPath(path: string): boolean {
  return isBackstagePodcastProgramPath(path) || isBackstagePodcastEpisodePath(path);
}

export function isPathInBackstagePodcast(path: string): boolean {
  return isBackstagePodcastParentPath(path) || isBackstagePodcastChildPath(path);
}

export function isBackstagePath(path: string): boolean {
  return (
    isBackstageGenrePath(path) ||
    isBackstageAlbumPath(path) ||
    isBackstageArtistPath(path) ||
    isBackstageTrackPath(path) ||
    isBackstageChildPagePath(path) ||
    isPlaylistPath(path) ||
    isBackstageStationPath(path) ||
    isPathInBackstagePodcast(path) ||
    isBackstageCuratorPath(path)
  );
}

export function isBackstageParentPath(path: string): boolean {
  const isValidBackstagePath = isBackstagePath(path);
  const isValidBackstagePodcastPath = isBackstagePodcastParentPath(path);
  return isValidBackstagePath || isValidBackstagePodcastPath;
}

export function isBackstageChildPath(path: string): boolean {
  const isValidBackstageChildPath = isBackstageChildPagePath(path);
  const isValidBackstagePodcastChildPath = isBackstagePodcastChildPath(path);
  return isValidBackstageChildPath || isValidBackstagePodcastChildPath;
}
